// Tiny shared manifest of every logo on disk with its file mtime.
// Used by brandLogoUrl + categoryLogoUrl to build cache-busted URLs
// (e.g. `…/brand-kyosho.png?v=1736209312`) so a freshly-uploaded logo
// instantly replaces the cached version everywhere on the storefront,
// no hard refresh needed.
//
// Strategy: fetch once on first call, cache the result in module
// memory + sessionStorage so subsequent calls are free. Refreshed on
// every page hard-load (good enough — admin uploads + visit is the
// flow we care about).

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'ogmini_logos_manifest_v1';

let cached = null;
let inflight = null;

function load() {
  if (cached) return cached;
  if (typeof sessionStorage !== 'undefined') {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.fetched_at && Date.now() - parsed.fetched_at < 60_000) {
          cached = parsed.logos || {};
          return cached;
        }
      }
    } catch {
      // ignore — fall through to refetch
    }
  }
  return null;
}

export function getLogoMtime(filename) {
  const m = load();
  if (!m) return null;
  const entry = m[filename];
  if (entry == null) return null;
  // Legacy shape: bare int mtime → return as-is so callers stay happy.
  if (typeof entry === 'number') return entry;
  // R2 shape: { url, v } — return v for back-compat with code that only
  // wants the cache-buster; callers that want the URL use getLogoEntry().
  return entry.v || null;
}

/** Returns the full manifest entry. Shape:
 *   number (legacy disk file's mtime), or
 *   { url, v } (R2-hosted — `url` survives deploys).
 *  null when the file isn't tracked yet.
 */
export function getLogoEntry(filename) {
  const m = load();
  if (!m) return null;
  return m[filename] || null;
}

export function ensureLogoManifest() {
  if (cached || load()) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = axios.get(`${API}/api/logos/manifest`)
    .then(({ data }) => {
      cached = data.logos || {};
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ logos: cached, fetched_at: Date.now() }));
      } catch { /* sessionStorage may be unavailable in private mode */ }
      return cached;
    })
    .catch(() => { cached = {}; return cached; })
    .finally(() => { inflight = null; });
  return inflight;
}

// Force a refresh — admin should call this after upload/delete so the
// next storefront view shows the new file even within the 60s window.
export function bustLogoManifest() {
  cached = null;
  inflight = null;
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}
